import { Type, Static } from '@sinclair/typebox';
import {
  defineSchema,
  SystemFields,
  StringField,
  IntegerField,
  EnumField,
  BooleanField,
  createDBIndex,
} from '@ayocore/platform/common/helpers/schema-types';

import { name as moduleName } from '../module.json';

/**
 * Folder Schema — папки Диска (общее файловое пространство компании).
 *
 * Дерево папок с единственным системным корнем COMPANY_DRIVE (parent_id NULL).
 * Папки — чистые метаданные: перемещение/переименование не трогает физические
 * S3-ключи файлов. Слой диска ортогонален слою вложений (uploaded_file_lnk).
 *
 * Модель доступов (фаза 2, хранение заложено сразу):
 * - access: явный режим папки; NULL — наследовать от предка;
 * - access_root_id: ближайший предок с явным access; NULL — «я сам корень прав».
 * Инвариант: (access IS NULL) XOR (access_root_id IS NULL).
 * В фазе 1 явный access есть только у корня (public_write) — всё пространство
 * доступно авторизованным, проверки прав по access не выполняются.
 */

export const FolderSchema = Type.Object({
  ...SystemFields,

  name: StringField({
    title: 'Название',
    minLength: 1,
    maxLength: 255,
    description: 'Отображаемое имя папки; уникально в пределах родителя',
  }),

  // Самосвязь: parent_id → filemanager_folders.id (через foreignKeys)
  parent_id: IntegerField({
    title: 'Родительская папка',
    nullable: true,
    description:
      'Ссылка на родительскую папку. NULL — только системный корень (создаёт DriveService, юзерский create запрещён правилом)',
  }),

  code: StringField({
    title: 'Системный код',
    maxLength: 100,
    nullable: true,
    description:
      'Служебный код системного якоря (напр. COMPANY_DRIVE). Поиск корня — по коду, имя — отображаемое',
  }),

  // nullable — канон для полей с default: иначе поле обязательно в create-типе
  // платформы (NULL трактуется как false)
  is_system: BooleanField({
    title: 'Системная',
    default: false,
    nullable: true,
    description: 'Создана системой (хуками): переименование/перемещение/удаление запрещены',
  }),

  access: EnumField(['private', 'public_read', 'public_write', 'custom'], {
    title: 'Доступ',
    nullable: true,
    description:
      'Явный режим доступа папки; NULL — наследовать от ближайшего предка с явным access. ' +
      'custom — права по ACL-записям (filemanager_folder_access): группы/пользователи с уровнями',
  }),

  // Самосвязь: access_root_id → filemanager_folders.id (через foreignKeys)
  access_root_id: IntegerField({
    title: 'Корень прав',
    nullable: true,
    description:
      'Ближайший предок с явным access (денормализация для O(1)-проверки прав); NULL — папка сама является корнем прав',
  }),
});

/**
 * Create/Update: юзеру доступны только name и parent_id. Служебные поля
 * (code, is_system) и поля модели доступов (access, access_root_id) через
 * generic RPC не задаются — их нормализует правило onBeforeCreate
 * (server/rules/folder_rules.ts), оно же наследует access_root_id от родителя.
 * ВАЖНО: серверная валидация create/update идёт по автогенерированным
 * схемам defineSchema (все колонки) — эти экспорты только для TS-типов форм.
 */
export const FolderCreateSchema = Type.Pick(FolderSchema, ['name', 'parent_id']);
export const FolderUpdateSchema = Type.Partial(FolderCreateSchema);

export type Folder = Static<typeof FolderSchema>;
export type FolderCreate = Static<typeof FolderCreateSchema>;
export type FolderUpdate = Static<typeof FolderUpdateSchema>;

export default defineSchema({
  moduleName,
  tableName: 'folders', // → filemanager_folders
  schema: FolderSchema,
  additionalIndexes: [
    'parent_id',
    'access_root_id',
    // Уникальность имён внутри папки (для корней parent_id IS NULL не работает —
    // NULL != NULL; корни страхует partial-индекс в release-скрипте)
    createDBIndex(['parent_id', 'name'], 'btree', true),
    // Уникальность системных кодов (NULL-ы не конфликтуют)
    createDBIndex(['code'], 'btree', true),
  ],
  foreignKeys: [
    {
      column: 'parent_id',
      references: { table: 'filemanager_folders', column: 'id' },
      // Удаление родителя с детьми — только через рекурсивную функцию deleteDriveFolder
      onDelete: 'RESTRICT',
    },
    {
      column: 'access_root_id',
      references: { table: 'filemanager_folders', column: 'id' },
      // Корень прав удаляется только вместе со своим поддеревом
      onDelete: 'RESTRICT',
    },
  ],
  dependencies: [],
});
